import { mean, median, percentile, stdDev } from '../geometry/statistics.js';
import { rgbAt } from '../image/analysis.js';
import type { NormalizedRect } from '../types/geometry.js';
import { clamp } from '../types/geometry.js';
import type { AnalysisBuffer } from '../types/image.js';
import type { FaceGeometryMetrics } from '../types/metrics.js';

/**
 * Shared region-of-interest helpers for the light/background quality checks.
 *
 * Everything here is a pure function over already-decoded pixels: no DOM, no
 * model, no state. The helpers exist so that the five checks added on top of
 * `QualityMetrics`/`BackgroundMetrics` measure the *same* regions the same way —
 * a highlight and a shadow on "the left cheek" must mean the identical
 * rectangle, otherwise the overlay and the report start disagreeing.
 */

export interface LumaStats {
  mean: number;
  median: number;
  stdDev: number;
  min: number;
  max: number;
  p05: number;
  p95: number;
  count: number;
}

export interface ColorMean {
  r: number;
  g: number;
  b: number;
}

export const clampRect = (rect: NormalizedRect): NormalizedRect => {
  const x = clamp(rect.x, 0, 1);
  const y = clamp(rect.y, 0, 1);
  return {
    x,
    y,
    width: clamp(rect.width, 0, 1 - x),
    height: clamp(rect.height, 0, 1 - y),
  };
};

export const rectArea = (rect: NormalizedRect): number =>
  Math.max(0, rect.width) * Math.max(0, rect.height);

export const expandRect = (rect: NormalizedRect, pad: number): NormalizedRect =>
  clampRect({
    x: rect.x - pad,
    y: rect.y - pad,
    width: rect.width + pad * 2,
    height: rect.height + pad * 2,
  });

export const pointInRect = (rect: NormalizedRect, x: number, y: number): boolean =>
  x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height;

const insideEllipse = (x: number, y: number, rect: NormalizedRect): boolean => {
  const rx = rect.width / 2;
  const ry = rect.height / 2;
  if (rx <= 0 || ry <= 0) return false;
  return ((x - (rect.x + rx)) / rx) ** 2 + ((y - (rect.y + ry)) / ry) ** 2 <= 1;
};

export interface CollectOptions {
  /** Restricts sampling to the ellipse inscribed in `rect`. */
  shape?: 'rect' | 'ellipse';
  /** Additional clip: only pixels inside this normalized ellipse are sampled. */
  clipEllipse?: NormalizedRect;
}

/** Collects the luma values of a normalized rectangle. Pure pixel scan. */
export const collectLuma = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  rect: NormalizedRect,
  options: CollectOptions = {},
): number[] => {
  if (width <= 0 || height <= 0) return [];
  const area = clampRect(rect);
  if (area.width <= 0 || area.height <= 0) return [];
  const x0 = clamp(Math.floor(area.x * width), 0, width - 1);
  const x1 = clamp(Math.ceil((area.x + area.width) * width), x0 + 1, width);
  const y0 = clamp(Math.floor(area.y * height), 0, height - 1);
  const y1 = clamp(Math.ceil((area.y + area.height) * height), y0 + 1, height);
  const ellipse = options.shape === 'ellipse';
  const clip = options.clipEllipse;
  const values: number[] = [];
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const nx = (x + 0.5) / width;
      const ny = (y + 0.5) / height;
      if (ellipse && !insideEllipse(nx, ny, area)) continue;
      if (clip && !insideEllipse(nx, ny, clip)) continue;
      values.push(luma[y * width + x] as number);
    }
  }
  return values;
};

export const lumaStats = (values: readonly number[]): LumaStats => {
  if (values.length === 0) {
    return { mean: 0, median: 0, stdDev: 0, min: 0, max: 0, p05: 0, p95: 0, count: 0 };
  }
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return {
    mean: mean(values),
    median: median(values),
    stdDev: stdDev(values),
    min,
    max,
    p05: percentile(values, 0.05),
    p95: percentile(values, 0.95),
    count: values.length,
  };
};

export const lumaStatsInRect = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  rect: NormalizedRect,
  options: CollectOptions = {},
): LumaStats => lumaStats(collectLuma(luma, width, height, rect, options));

/** Share of samples at or above `threshold` (0..1). */
export const clippedRatio = (values: readonly number[], threshold: number): number => {
  if (values.length === 0) return 0;
  let clipped = 0;
  for (const value of values) {
    if (value >= threshold) clipped += 1;
  }
  return clipped / values.length;
};

export const clippedRatioInRect = (
  luma: Uint8ClampedArray,
  width: number,
  height: number,
  rect: NormalizedRect,
  threshold: number,
  options: CollectOptions = {},
): number => clippedRatio(collectLuma(luma, width, height, rect, options), threshold);

export const rgbMeanInRect = (
  buffer: AnalysisBuffer,
  rect: NormalizedRect,
  options: CollectOptions = {},
): ColorMean => {
  const area = clampRect(rect);
  if (area.width <= 0 || area.height <= 0 || buffer.width <= 0 || buffer.height <= 0) {
    return { r: 0, g: 0, b: 0 };
  }
  const x0 = clamp(Math.floor(area.x * buffer.width), 0, buffer.width - 1);
  const x1 = clamp(Math.ceil((area.x + area.width) * buffer.width), x0 + 1, buffer.width);
  const y0 = clamp(Math.floor(area.y * buffer.height), 0, buffer.height - 1);
  const y1 = clamp(Math.ceil((area.y + area.height) * buffer.height), y0 + 1, buffer.height);
  const ellipse = options.shape === 'ellipse';
  let sumR = 0;
  let sumG = 0;
  let sumB = 0;
  let count = 0;
  for (let y = y0; y < y1; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const nx = (x + 0.5) / buffer.width;
      const ny = (y + 0.5) / buffer.height;
      if (ellipse && !insideEllipse(nx, ny, area)) continue;
      const rgb = rgbAt(buffer.data, buffer.width, buffer.channels, x, y);
      sumR += rgb.r;
      sumG += rgb.g;
      sumB += rgb.b;
      count += 1;
    }
  }
  if (count === 0) return { r: 0, g: 0, b: 0 };
  return { r: sumR / count, g: sumG / count, b: sumB / count };
};

/** Max-minus-min of the three channels; 0 for a neutral grey. */
export const channelSpread = (color: ColorMean): number =>
  Math.max(color.r, color.g, color.b) - Math.min(color.r, color.g, color.b);

/** Uniform grid of normalized cells covering `rect`. */
export const gridRects = (rect: NormalizedRect, cols: number, rows: number): NormalizedRect[] => {
  const out: NormalizedRect[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      out.push({
        x: rect.x + (col / cols) * rect.width,
        y: rect.y + (row / rows) * rect.height,
        width: rect.width / cols,
        height: rect.height / rows,
      });
    }
  }
  return out;
};

// ---------------------------------------------------------------------------
// Skin regions
// ---------------------------------------------------------------------------

export type SkinRegionId = 'forehead' | 'left_cheek' | 'right_cheek' | 'nose' | 'eye_band';

export const SKIN_REGION_IDS: readonly SkinRegionId[] = [
  'forehead',
  'left_cheek',
  'right_cheek',
  'nose',
  'eye_band',
];

export interface SkinRegion {
  id: SkinRegionId;
  rect: NormalizedRect;
}

/**
 * The face oval, approximated from landmarks: hairline → chin, temple to temple.
 *
 * `faceBox` is the bounding box of the *mesh*, which reaches above the hairline
 * and therefore contains hair; the drawn/document face starts at
 * `foreheadPoint`. Everything skin-related is clipped to this oval so hair and
 * the white wall in the box corners can never be measured as skin.
 */
export const faceSkinEllipse = (geometry: FaceGeometryMetrics): NormalizedRect =>
  clampRect({
    x: geometry.faceCenterX - geometry.faceBox.width / 2,
    y: geometry.foreheadPoint.y,
    width: geometry.faceBox.width,
    height: Math.max(1e-3, geometry.chinPoint.y - geometry.foreheadPoint.y),
  });

/**
 * Skin regions derived from the face mesh.
 *
 * The regions are anchored on landmarks (forehead, nose, eye line) and on
 * `faceCenterX`, and the callers additionally clip them to `faceSkinEllipse` —
 * the bounding box of a face always contains background in its corners.
 */
export const skinRegions = (geometry: FaceGeometryMetrics): SkinRegion[] => {
  const skin = faceSkinEllipse(geometry);
  const w = Math.max(skin.width, 1e-3);
  const h = Math.max(skin.height, 1e-3);
  const cx = skin.x + skin.width / 2;
  const region = (id: SkinRegionId, rect: NormalizedRect): SkinRegion => ({
    id,
    rect: clampRect(rect),
  });
  return [
    // Below the hairline landmark, above the eye line.
    region('forehead', {
      x: cx - w * 0.17,
      y: skin.y + h * 0.1,
      width: w * 0.34,
      height: h * 0.1,
    }),
    region('left_cheek', {
      x: skin.x + w * 0.06,
      y: skin.y + h * 0.62,
      width: w * 0.3,
      height: h * 0.24,
    }),
    region('right_cheek', {
      x: skin.x + w * 0.64,
      y: skin.y + h * 0.62,
      width: w * 0.3,
      height: h * 0.24,
    }),
    region('nose', {
      x: geometry.noseTip.x - w * 0.11,
      y: geometry.noseTip.y - h * 0.06,
      width: w * 0.22,
      height: h * 0.18,
    }),
    // A thin band centred on the eye line — the brightest, most specular part.
    region('eye_band', {
      x: cx - w * 0.36,
      y: geometry.eyeLineFromTop - h * 0.07,
      width: w * 0.72,
      height: h * 0.14,
    }),
  ];
};

// ---------------------------------------------------------------------------
// Subject mask (what is *not* background)
// ---------------------------------------------------------------------------

export interface SubjectMask {
  /** Head incl. hair, widened sideways and upwards. */
  head: NormalizedRect;
  /** Everything below the chin — neck, shoulders, clothing. */
  torso: NormalizedRect;
  rects: NormalizedRect[];
}

/**
 * Re-derives the subject region the background analysis excludes.
 *
 * `src/background/analyze.ts` keeps its helper private, so the same rule is
 * restated here: the head is padded for hair, and everything below the chin is
 * the subject's body, because a portrait never shows background under the chin.
 */
export const buildSubjectMask = (input: {
  geometry?: FaceGeometryMetrics;
  subjectRect?: NormalizedRect;
}): SubjectMask | null => {
  const base = input.geometry
    ? {
        x: input.geometry.headBox.x,
        y: input.geometry.headBox.y,
        width: input.geometry.headBox.width,
        height: input.geometry.headBox.height,
      }
    : input.subjectRect;
  if (!base || base.width <= 0 || base.height <= 0) return null;

  const side = base.width * 0.14;
  const top = base.height * 0.35;
  const head = clampRect({
    x: base.x - side,
    y: base.y - top,
    width: base.width + side * 2,
    height: base.height + top + base.height * 0.1,
  });
  const torsoTop = clamp(base.y + base.height * 0.9, 0, 1);
  const torso: NormalizedRect = { x: 0, y: torsoTop, width: 1, height: Math.max(0, 1 - torsoTop) };
  return { head, torso, rects: [head, torso] };
};

export const isInsideSubject = (
  mask: SubjectMask | null,
  x: number,
  y: number,
): boolean =>
  mask !== null && (pointInRect(mask.head, x, y) || pointInRect(mask.torso, x, y));

// ---------------------------------------------------------------------------
// Background surface (grid + halo)
// ---------------------------------------------------------------------------

export interface BackgroundCell {
  rect: NormalizedRect;
  meanLuma: number;
  meanColor: ColorMean;
  pixels: number;
}

export interface BackgroundSurface {
  cells: BackgroundCell[];
  /** Median luma of the background cells — the local "what background is". */
  referenceLuma: number;
  /** Spread of the per-column / per-row cell means (0 = perfectly even). */
  horizontalGradient: number;
  verticalGradient: number;
  backgroundPixels: number;
  /** Share of the dark pixels in the thin band right around the head. */
  haloRatio: number;
  haloPixels: number;
  haloMeanLuma: number;
}

const EMPTY_SURFACE: BackgroundSurface = {
  cells: [],
  referenceLuma: 0,
  horizontalGradient: 0,
  verticalGradient: 0,
  backgroundPixels: 0,
  haloRatio: 0,
  haloPixels: 0,
  haloMeanLuma: 0,
};

const spread = (values: readonly number[]): number => {
  if (values.length < 2) return 0;
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (value < min) min = value;
    if (value > max) max = value;
  }
  return max - min;
};

/**
 * Coarse, subject-aware description of the background.
 *
 * The grid gives a uniformity/gradient picture without trusting a single ring
 * sample, and the halo band exposes the classic "dark outline around a pasted
 * subject" contamination. Both background checks share this measurement.
 */
export const analyzeBackgroundSurface = (input: {
  buffer: AnalysisBuffer;
  subjectRect: NormalizedRect;
  geometry?: FaceGeometryMetrics;
  grid?: number;
  haloWidth?: number;
  haloDarkDelta?: number;
}): BackgroundSurface => {
  const { buffer } = input;
  if (buffer.width <= 0 || buffer.height <= 0) return EMPTY_SURFACE;

  const grid = clamp(Math.round(input.grid ?? 8), 3, 16);
  const mask = buildSubjectMask({
    ...(input.geometry ? { geometry: input.geometry } : {}),
    subjectRect: input.subjectRect,
  });

  const cells: BackgroundCell[] = [];
  const columnMeans: number[][] = Array.from({ length: grid }, () => []);
  const rowMeans: number[][] = Array.from({ length: grid }, () => []);
  let backgroundPixels = 0;

  for (let row = 0; row < grid; row += 1) {
    for (let col = 0; col < grid; col += 1) {
      const rect: NormalizedRect = {
        x: col / grid,
        y: row / grid,
        width: 1 / grid,
        height: 1 / grid,
      };
      const centerX = rect.x + rect.width / 2;
      const centerY = rect.y + rect.height / 2;
      if (isInsideSubject(mask, centerX, centerY)) continue;
      const values = collectLuma(buffer.luma, buffer.width, buffer.height, rect);
      if (values.length < 8) continue;
      const stats = lumaStats(values);
      cells.push({
        rect,
        meanLuma: stats.mean,
        meanColor: rgbMeanInRect(buffer, rect),
        pixels: values.length,
      });
      (columnMeans[col] as number[]).push(stats.mean);
      (rowMeans[row] as number[]).push(stats.mean);
      backgroundPixels += values.length;
    }
  }

  if (cells.length === 0) return EMPTY_SURFACE;

  const referenceLuma = median(cells.map((cell) => cell.meanLuma));
  const columnAverages = columnMeans.filter((list) => list.length > 0).map((list) => mean(list));
  const rowAverages = rowMeans.filter((list) => list.length > 0).map((list) => mean(list));

  let haloPixels = 0;
  let haloDark = 0;
  let haloSum = 0;
  if (mask) {
    const haloWidth = Math.max(0.005, input.haloWidth ?? 0.035);
    const haloDarkDelta = input.haloDarkDelta ?? 45;
    const outer = expandRect(mask.head, haloWidth);
    const x0 = clamp(Math.floor(outer.x * buffer.width), 0, buffer.width - 1);
    const x1 = clamp(Math.ceil((outer.x + outer.width) * buffer.width), x0 + 1, buffer.width);
    const y0 = clamp(Math.floor(outer.y * buffer.height), 0, buffer.height - 1);
    const y1 = clamp(Math.ceil((outer.y + outer.height) * buffer.height), y0 + 1, buffer.height);
    for (let y = y0; y < y1; y += 1) {
      for (let x = x0; x < x1; x += 1) {
        const nx = (x + 0.5) / buffer.width;
        const ny = (y + 0.5) / buffer.height;
        if (isInsideSubject(mask, nx, ny)) continue;
        const value = buffer.luma[y * buffer.width + x] as number;
        haloPixels += 1;
        haloSum += value;
        if (value < referenceLuma - haloDarkDelta) haloDark += 1;
      }
    }
  }

  return {
    cells,
    referenceLuma,
    horizontalGradient: spread(columnAverages),
    verticalGradient: spread(rowAverages),
    backgroundPixels,
    haloRatio: haloPixels > 0 ? haloDark / haloPixels : 0,
    haloPixels,
    haloMeanLuma: haloPixels > 0 ? haloSum / haloPixels : 0,
  };
};
